import React, { useCallback, useMemo, useId, useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Label from './Label';
import dayjs, { Dayjs } from 'dayjs';
import { ILabel } from '../constants/interfaces';
import { inputMaxWidth } from '../constants/globalcss';
import { MIN_ACCEPTED_DATE } from '../constants/app';
import { getBaseTextFieldStyles } from '../styles/fieldStyles';

/**
 * Props for the DateField component
 */
export interface IDateFieldProps {
  /** Current date value in ISO string format (YYYY-MM-DD) */
  value: string;
  /** Callback fired when the date changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Callback to report validation errors to parent form */
  onError?: (error: string) => void;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Width of the date picker */
  width?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Minimum selectable date */
  minDate?: string;
  /** Maximum selectable date */
  maxDate?: string;
}

/**
 * Utility functions for date handling
 */
const dateUtils = {
  /**
   * Formats a dayjs object to ISO date string (YYYY-MM-DD)
   */
  formatToISODate: (date: Dayjs | null): string => {
    if (!date || !date.isValid()) {
      return '';
    }
    return date.format('YYYY-MM-DD');
  },

  /**
   * Validates if a date string is in valid format
   */
  isValidDateString: (dateString: string): boolean => {
    if (!dateString) return true; // Empty is valid (optional field)
    const parsed = dayjs(dateString);
    return parsed.isValid();
  },

  /**
   * Safely parses a date string and returns a dayjs object or null
   */
  parseDate: (dateString: string | null | undefined): Dayjs | null => {
    if (!dateString || dateString.trim() === '') {
      return null;
    }

    try {
      const parsed = dayjs(dateString);
      return parsed.isValid() ? parsed : null;
    } catch {
      return null;
    }
  },
};

const resolveMinDate = (candidate?: string): Dayjs => {
  const globalMin = dayjs(MIN_ACCEPTED_DATE).startOf('day');
  if (!candidate) {
    return globalMin;
  }

  const parsed = dayjs(candidate).startOf('day');
  if (!parsed.isValid()) {
    return globalMin;
  }

  return parsed.isAfter(globalMin) ? parsed : globalMin;
};

/**
 * DateField Component
 *
 * A reusable date picker component that integrates with form validation
 * and provides a consistent interface for date input across the application.
 *
 * Features:
 * - Proper date validation and error handling
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Support for min/max date constraints
 * - Rules-based validation with user-friendly feedback
 * - Improved typing experience - doesn't clear fields during partial input
 * - Calendar picker respects min/max constraints automatically
 * - Validation on blur for complete date checking
 * - Integrated with form validation - prevents submission with invalid dates
 * - Memoized performance optimizations
 *
 * @example
 * ```tsx
 * <DateField
 *   value="2023-12-25"
 *   onChange={handleDateChange}
 *   field={formField}
 *   errorText={validationError}
 * />
 * ```
 *
 * @example
 * // Field with rules to enforce minimum date as current date
 * ```tsx
 * const fieldWithRules = {
 *   ...formField,
 *   rules: JSON.stringify({ "date": { "min": "current" } })
 * };
 *
 * <DateField
 *   value=""
 *   onChange={handleDateChange}
 *   field={fieldWithRules}
 * />
 * ```
 */
function DateField(props: IDateFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    width,
    disabled = false,
    readOnly = false,
    placeholder,
    minDate,
    maxDate,
    onError,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Memoize parsed date value to avoid re-parsing on every render
  const parsedValue = useMemo(() => {
    return dateUtils.parseDate(value);
  }, [value]);

  // Memoize min/max date constraints including rules-based constraints
  const constraints = useMemo(() => {
    let effectiveMinDate = minDate;
    let effectiveMaxDate = maxDate;

    // Parse field rules for date constraints from backend
    if (field.rules) {
      try {
        const rules = JSON.parse(field.rules);
        if (rules.date) {
          // Use the date provided by backend directly
          if (rules.date.min) {
            effectiveMinDate = rules.date.min;
          }
          // Use the max date provided by backend directly
          if (rules.date.max) {
            effectiveMaxDate = rules.date.max;
          }
        }
      } catch (error) {
        console.warn(
          'Failed to parse field rules for date constraints:',
          error
        );
      }
    }

    return {
      maxDate: effectiveMaxDate
        ? dateUtils.parseDate(effectiveMaxDate) || undefined
        : undefined,
      minDate: resolveMinDate(effectiveMinDate),
    };
  }, [minDate, maxDate, field.rules]);

  // Helper function to get validation error message for current rules
  const getValidationErrorMessage = useCallback(
    (dateValue?: Dayjs | null): string => {
      const minAccepted = dayjs(MIN_ACCEPTED_DATE).startOf('day');
      const inputDate = dateValue?.startOf('day');
      if (inputDate?.isBefore(minAccepted)) {
        return `Date cannot be less than ${minAccepted.format('MM/DD/YYYY')}`;
      }

      if (!field.rules) return '';

      try {
        const rules = JSON.parse(field.rules);
        if (rules.date?.min) {
          const minDate = dayjs(rules.date.min).format('MM/DD/YYYY');
          return `Date cannot be less than ${minDate}`;
        } else if (rules.date?.max) {
          const maxDate = dayjs(rules.date.max).format('MM/DD/YYYY');
          return `Date cannot be greater than ${maxDate}`;
        }
      } catch (error) {
        return 'Invalid date';
      }

      return '';
    },
    [field.rules]
  );

  // Validation function to check if date meets rule constraints
  const validateDateAgainstRules = useCallback(
    (dateValue: Dayjs | null): boolean => {
      if (!dateValue) return true;

      const minAccepted = dayjs(MIN_ACCEPTED_DATE).startOf('day');
      const inputDate = dateValue.startOf('day');
      if (minAccepted.isValid() && inputDate.isBefore(minAccepted)) {
        return false;
      }

      if (!field.rules) return true;

      try {
        const rules = JSON.parse(field.rules);
        if (rules.date) {
          // Check minimum date constraint
          if (rules.date.min) {
            const minDate = dayjs(rules.date.min).startOf('day');
            const inputDate = dateValue.startOf('day');
            if (minDate.isValid() && inputDate.isBefore(minDate)) {
              return false;
            }
          }
          // Check maximum date constraint
          if (rules.date.max) {
            const maxDate = dayjs(rules.date.max).startOf('day');
            const inputDate = dateValue.startOf('day');
            if (maxDate.isValid() && inputDate.isAfter(maxDate)) {
              return false;
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse field rules for date validation:', error);
      }

      return true;
    },
    [field.rules]
  );

  // State to track rule validation errors
  const [ruleValidationError, setRuleValidationError] = useState<string>('');

  // Validate current value when it changes externally or rules change
  useEffect(() => {
    if (parsedValue && !validateDateAgainstRules(parsedValue)) {
      const errorMessage = getValidationErrorMessage(parsedValue);
      setRuleValidationError(errorMessage);
      onError?.(errorMessage); // Report error to parent form
    } else {
      setRuleValidationError('');
      onError?.(''); // Clear error in parent form
    }
  }, [
    parsedValue,
    field.rules,
    validateDateAgainstRules,
    getValidationErrorMessage,
    onError,
  ]);

  // Handle validation on blur (when user finishes typing)
  const handleBlur = useCallback(() => {
    if (parsedValue && !validateDateAgainstRules(parsedValue)) {
      const errorMessage = getValidationErrorMessage(parsedValue);
      setRuleValidationError(errorMessage);
      onError?.(errorMessage); // Report error to parent form
    }
  }, [
    parsedValue,
    field.rules,
    validateDateAgainstRules,
    getValidationErrorMessage,
    onError,
  ]);

  // Optimized change handler with useCallback to prevent unnecessary re-renders
  const handleDateChange = useCallback(
    (newValue: Dayjs | null) => {
      // Only update if we have a valid complete date or if clearing the field
      if (!newValue) {
        // Clearing the field
        onChange('');
        setRuleValidationError('');
        onError?.(''); // Clear error in parent form
      } else if (newValue.isValid()) {
        // Valid complete date
        const formattedDate = dateUtils.formatToISODate(newValue);
        onChange(formattedDate);

        // Validate and show error if needed
        if (!validateDateAgainstRules(newValue)) {
          const errorMessage = getValidationErrorMessage(newValue);
          setRuleValidationError(errorMessage);
          onError?.(errorMessage); // Report error to parent form
        } else {
          setRuleValidationError('');
          onError?.(''); // Clear error in parent form
        }
      }
      // For invalid intermediate states (while typing), do nothing - let user continue typing
    },
    [
      onChange,
      validateDateAgainstRules,
      field.rules,
      getValidationErrorMessage,
      onError,
    ]
  );

  // Determine if there's an error to show
  const hasError = Boolean(errorText || ruleValidationError);

  // Generate helper text for accessibility
  const helperText = useMemo(() => {
    if (ruleValidationError) return ruleValidationError;
    if (errorText) return errorText;
    return '';
  }, [errorText, ruleValidationError]);

  const resolvedWidth = width != null ? String(width) : String(inputMaxWidth);
  const isFullWidth = width === '100%' || resolvedWidth === '100%';

  return (
    <>
      {/* Compact calendar icon styles - applied globally to avoid specificity issues */}
      <style>
        {`
                    /* Compact calendar icon styles for all date fields */
                    .MuiDatePicker-root .MuiIconButton-root {
                        width: 28px !important;
                        height: 28px !important;
                        padding: 0px !important;
                        margin: 0px !important;
                        min-width: 28px !important;
                    }
                    
                    .MuiDatePicker-root .MuiSvgIcon-root {
                        width: 20px !important;
                        height: 20px !important;
                    }
                `}
      </style>

      {/* Label with proper accessibility attributes */}
      {!isLabelHidden && (
        <Label
          text={field.text}
          required={field.isMandatory}
          htmlFor={fieldId}
        />
      )}

      {/* Date picker with enhanced props - styled like other input components */}
      <DatePicker
        value={parsedValue}
        onChange={handleDateChange}
        disabled={disabled}
        readOnly={readOnly}
        minDate={constraints.minDate}
        maxDate={constraints.maxDate}
        format="MM/DD/YYYY"
        sx={{
          maxWidth: '100%',
          width: isFullWidth ? '100%' : resolvedWidth,
        }}
        slotProps={{
          popper: {
            placement: 'bottom-start',
          },
          textField: {
            'aria-describedby': hasError ? errorId : undefined,
            'aria-invalid': hasError,
            'aria-labelledby': isLabelHidden ? undefined : labelId,
            'aria-required': field.isMandatory,
            error: hasError,
            fullWidth: isFullWidth,
            helperText: helperText,
            id: fieldId,
            inputProps: {
              'aria-label': isLabelHidden ? field.text : undefined,
            },
            onBlur: handleBlur,
            placeholder: placeholder,
            size: 'small',
            sx: getBaseTextFieldStyles(resolvedWidth),
          },
        }}
      />
    </>
  );
}

export default DateField;

// Export utilities for testing and external use
export { dateUtils };
